import { useMemo, useState } from 'react'
import { SegmentedControl } from '@mantine/core'
import { fillLasted, fillWhere, formatClock, formatLitres } from '@racedash/shared'
import type { DashboardData } from '../../dashboard/useSessionData'
import type { ScreenContext } from '../../dashboard/screenContext'
import { useFuelBurn } from '../../dashboard/useFuelBurn'
import { BurnView } from './BurnView'
import './fuel.css'

/**
 * Every fill this session, newest first.
 *
 * The team's own cars by default, because that is the list anybody at the pit
 * wall is reading; the whole field is one click away, since the fuel station
 * publishes everybody's and how much a rival takes on is a fair question.
 *
 * The columns follow the fuel: when and how much, the stop it went in at, the
 * laps since the fuel before it and what that works out per lap, and how far
 * this fuel went — or has gone so far, for the fill still in the car.
 *
 * Beside it, the burn: what this car uses a lap, green and under a yellow,
 * from every full tank of the event rather than this session's alone.
 */
export default function FuelScreen({
  data,
  car,
  context,
}: {
  data: DashboardData
  car: string | null
  context: ScreenContext
}) {
  const ours = context.ourCars
  const [scope, setScope] = useState<'ours' | 'all'>(ours.length > 0 ? 'ours' : 'all')
  // Burn needs the team's sessions, so a share link has the fills alone.
  const canBurn = !!context.fuel && !!car
  const [view, setView] = useState<'burn' | 'fills'>('burn')
  const showBurn = canBurn && view === 'burn'
  const burn = useFuelBurn(showBurn ? context.fuel : undefined, car)

  const rows = useMemo(() => {
    const keep = scope === 'ours' && ours.length > 0 ? new Set(ours) : null
    return data.fuel
      .filter((f) => !keep || keep.has(f.car))
      .sort((a, b) => b.at.toMillis() - a.at.toMillis())
  }, [data.fuel, scope, ours])

  const total = rows.reduce((sum, f) => sum + f.litres, 0)

  return (
    <div className="fuel-screen">
      <div className="fuel-screen-head">
        {canBurn && (
          <SegmentedControl
            size="xs"
            value={view}
            onChange={(v) => setView(v as 'burn' | 'fills')}
            data={[
              { value: 'burn', label: `Burn · car ${car}` },
              { value: 'fills', label: 'Fills this session' },
            ]}
          />
        )}
        {!showBurn && (
          <>
            <SegmentedControl
              size="xs"
              value={scope}
              onChange={(v) => setScope(v as 'ours' | 'all')}
              data={[
                { value: 'ours', label: 'Our cars', disabled: ours.length === 0 },
                { value: 'all', label: 'Every car' },
              ]}
            />
            <span className="fuel-screen-total">
              {rows.length} fill{rows.length === 1 ? '' : 's'} · {formatLitres(total)}
            </span>
          </>
        )}
      </div>

      {showBurn ? (
        <BurnView car={car!} fuel={context.fuel!} {...burn} />
      ) : rows.length === 0 ? (
        <div className="fuel-screen-empty">
          {data.fuel.length === 0
            ? 'No fills for this session. Fuel is captured only for events whose administrator has set a fuel source.'
            : 'None of our cars has taken fuel in this session.'}
        </div>
      ) : (
        <div className="fuel-screen-scroll">
          <table className="fuel-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Car</th>
                <th className="num">Litres</th>
                <th>Where</th>
                <th className="num">Laps since</th>
                <th className="num">L/lap</th>
                <th className="num">Lasted</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr
                  key={`${f.car}-${f.at.toMillis()}`}
                  className={f.car === car ? 'fuel-table-ours' : undefined}
                  title={f.raw ? `${f.source}: ${f.raw}` : f.source}
                >
                  <td className="mono">{formatClock(f.at.toDate(), { seconds: true })}</td>
                  <td className="mono">{f.car}</td>
                  <td className="num mono">{f.litres.toFixed(2)}</td>
                  <td className={f.kind === 'unmatched' ? 'fuel-table-muted' : undefined}>{fillWhere(f)}</td>
                  <td className="num mono">
                    {f.lapsSincePrevious != null && f.lapsSincePrevious > 0
                      ? `${f.lapsSincePrevious}${f.sinceStart ? '*' : ''}`
                      : '—'}
                  </td>
                  <td className="num mono">{f.litresPerLap != null ? f.litresPerLap.toFixed(2) : '—'}</td>
                  <td className="num mono">{fillLasted(f) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.some((f) => f.sinceStart) && (
            <p className="fuel-screen-note">* from the start of the session, assuming the car started full.</p>
          )}
        </div>
      )}
    </div>
  )
}
